import { Invitation, SessionState } from 'sip.js';
const earlyMediaWatcherCleanup = new WeakMap();
/** Retry delays — ontrack often fires after setAnswer resolves on 183. */
const EARLY_MEDIA_ATTACH_RETRY_MS = [0, 50, 150, 300, 600, 1000, 2000, 4000];
/**
 * Outbound INVITE: play remote RTP from 183 Session Progress (and similar provisional SDP)
 * before 200 OK or a final 4xx/5xx/6xx such as 480. Without this, SIP.js only attaches
 * remote audio after the call is Established.
 */
export const OUTBOUND_INVITER_OPTIONS = {
    earlyMedia: true,
};
/** Provisional responses that may carry early-media SDP. */
export function isEarlyMediaProgressResponse(statusCode) {
    return statusCode === 180 || statusCode === 183;
}
/** Final responses that mean the callee is busy or unavailable. */
export function isLineBusyRejectStatus(statusCode) {
    if (statusCode == null)
        return false;
    return statusCode === 480 || statusCode === 486 || statusCode === 600 || statusCode === 603;
}
export function createOutboundProgressTracker() {
    return { saw180: false, provisional183Count: 0 };
}
/**
 * Outbound UI phase from a 1xx INVITE response.
 * - 180 / first 183 → ringing (callee phone ringing / ringback early media)
 * - second+ 183 after ringback → line-busy (carrier post-ring IVR, e.g. not accessible)
 */
export function outboundPhaseFromProgress(statusCode, tracker) {
    if (statusCode === 180) {
        tracker.saw180 = true;
        return 'ringing';
    }
    if (statusCode === 183) {
        tracker.provisional183Count += 1;
        if (tracker.provisional183Count >= 2) {
            return 'line-busy';
        }
        return 'ringing';
    }
    return null;
}
function collectLiveAudioTracks(session) {
    const sdh = session.sessionDescriptionHandler;
    const tracks = [];
    const seen = new Set();
    const addTrack = (track) => {
        if (!track || track.kind !== 'audio' || track.readyState === 'ended' || seen.has(track.id)) {
            return;
        }
        track.enabled = true;
        seen.add(track.id);
        tracks.push(track);
    };
    sdh?.remoteMediaStream?.getAudioTracks().forEach(addTrack);
    sdh?.peerConnection?.getReceivers().forEach((receiver) => {
        addTrack(receiver.track ?? undefined);
    });
    return tracks;
}
/** Attach remote audio tracks from peerConnection receivers / remoteMediaStream. */
export function attachRemoteAudioEarlyMedia(session, audioElement) {
    if (!session || !audioElement)
        return false;
    const audioTracks = collectLiveAudioTracks(session);
    if (audioTracks.length === 0)
        return false;
    const stream = new MediaStream(audioTracks);
    audioElement.autoplay = true;
    audioElement.muted = false;
    audioElement.srcObject = stream;
    void audioElement.play().catch(() => undefined);
    return true;
}
/**
 * Watch an outbound Inviter for early-media audio: attach immediately on 183, retry while ICE
 * connects, and listen for late ontrack events (SessionManager often runs before tracks exist).
 */
export function watchEarlyMediaAudio(session, audioElement) {
    earlyMediaWatcherCleanup.get(session)?.();
    let cancelled = false;
    const retryTimers = [];
    const tryAttach = () => {
        if (cancelled || session.state === SessionState.Terminated)
            return;
        attachRemoteAudioEarlyMedia(session, audioElement);
    };
    const onPeerTrack = (event) => {
        if (event.track.kind === 'audio')
            tryAttach();
    };
    const onRemoteStreamAddTrack = () => tryAttach();
    const bindPeerConnection = () => {
        const sdh = session.sessionDescriptionHandler;
        const pc = sdh?.peerConnection;
        if (pc) {
            pc.addEventListener('track', onPeerTrack);
        }
        sdh?.remoteMediaStream?.addEventListener('addtrack', onRemoteStreamAddTrack);
        return { pc, remoteStream: sdh?.remoteMediaStream };
    };
    let bindings = bindPeerConnection();
    const rebindTimer = window.setInterval(() => {
        if (cancelled || session.state === SessionState.Terminated)
            return;
        const sdh = session.sessionDescriptionHandler;
        const pc = sdh?.peerConnection;
        if (pc && pc !== bindings.pc) {
            bindings.pc?.removeEventListener('track', onPeerTrack);
            bindings.remoteStream?.removeEventListener('addtrack', onRemoteStreamAddTrack);
            bindings = bindPeerConnection();
        }
    }, 100);
    EARLY_MEDIA_ATTACH_RETRY_MS.forEach((delayMs) => {
        retryTimers.push(window.setTimeout(tryAttach, delayMs));
    });
    const onStateChange = (state) => {
        if (state === SessionState.Terminated)
            cleanup();
    };
    session.stateChange.addListener(onStateChange);
    const cleanup = () => {
        if (cancelled)
            return;
        cancelled = true;
        window.clearInterval(rebindTimer);
        retryTimers.forEach((timerId) => window.clearTimeout(timerId));
        bindings.pc?.removeEventListener('track', onPeerTrack);
        bindings.remoteStream?.removeEventListener('addtrack', onRemoteStreamAddTrack);
        session.stateChange.removeListener(onStateChange);
        earlyMediaWatcherCleanup.delete(session);
    };
    earlyMediaWatcherCleanup.set(session, cleanup);
    tryAttach();
    return cleanup;
}
/** Inviter invite requestDelegate.onProgress — start early-media audio playback. */
export function handleOutboundEarlyMediaProgress(response, session, audioElement) {
    if (!isEarlyMediaProgressResponse(response.message?.statusCode) || !session || !audioElement) {
        return;
    }
    watchEarlyMediaAudio(session, audioElement);
}
/** Max wait for REFER subscription NOTIFY (sipfrag) before surfacing a timeout hint. */
const REFER_NOTIFY_TIMEOUT_MS = 60_000;
function parseSipfragStatus(body) {
    const line = body
        .trim()
        .split(/\r?\n/)
        .find((l) => /^SIP\/2\.0\s+\d{3}/i.test(l.trim())) ?? body.trim();
    const match = line.match(/^SIP\/2\.0\s+(\d{3})(?:\s+(.*))?/i);
    if (!match)
        return undefined;
    return { statusCode: Number(match[1]), phrase: match[2]?.trim() };
}
function isReferSubscriptionTerminated(subscriptionState) {
    return Boolean(subscriptionState && /^terminated\b/i.test(subscriptionState.trim()));
}
/**
 * SIP REFER options that accept in-dialog NOTIFY (RFC 3515 sipfrag) and resolve when the
 * transfer attempt reaches a terminal outcome. Keeps the session established until then so
 * Asterisk/PBX refer subscriptions are not answered with 481.
 */
export function createReferAwaitOptions(timeoutMs = REFER_NOTIFY_TIMEOUT_MS) {
    let resolveWait;
    const waitPromise = new Promise((resolve) => {
        resolveWait = resolve;
    });
    let settled = false;
    const settle = (result) => {
        if (settled)
            return;
        settled = true;
        window.clearTimeout(timer);
        resolveWait(result);
    };
    const timer = window.setTimeout(() => settle({ outcome: 'timeout' }), timeoutMs);
    const onNotify = (notification) => {
        const status = parseSipfragStatus(notification.request.body ?? '');
        const subscriptionState = notification.request.getHeader('Subscription-State');
        void notification.accept().catch(() => undefined);
        if (status) {
            if (status.statusCode >= 200 && status.statusCode < 300) {
                settle({ outcome: 'success', statusCode: status.statusCode });
                return;
            }
            if (status.statusCode >= 300) {
                const detail = status.phrase ? `${status.statusCode} ${status.phrase}` : String(status.statusCode);
                settle({ outcome: 'failure', statusCode: status.statusCode, message: `Transfer failed: ${detail}` });
                return;
            }
        }
        if (isReferSubscriptionTerminated(subscriptionState)) {
            if (status && status.statusCode >= 200 && status.statusCode < 300) {
                settle({ outcome: 'success', statusCode: status.statusCode });
            }
            else {
                settle({
                    outcome: 'failure',
                    statusCode: status?.statusCode,
                    message: 'Transfer subscription ended before success.',
                });
            }
        }
    };
    return {
        onNotify,
        waitForReferNotify: () => waitPromise,
        cancelReferWait: (message) => settle({ outcome: 'failure', message }),
    };
}
/** If REGISTER neither succeeds nor rejects (e.g. WS up but registrar silent), surfacing the usual cause avoids an endless spinner. */
export const REGISTER_PENDING_HINT_MS = 15_000;
/** PBX SDP must advertise DTLS-SRTP (`a=fingerprint`). Shown when setRemoteDescription fails for that reason. */
export const SDP_MISSING_DTLS_FINGERPRINT_HINT = 'WebRTC media error: SDP had no DTLS fingerprint (browser requires DTLS-SRTP). Configure this extension’s PJSIP endpoint for WebRTC on Asterisk (e.g. webrtc=yes, or media_encryption=dtls with working dtls certs; avoid SDES-only offers). Plain SIP desk phones are more permissive than the browser.';
/**
 * Inbound answer: cap how long the WebRTC layer waits for ICE gathering before sending 200 OK.
 * Without this, browsers often wait several seconds before the call appears “connected” / audio flows.
 */
export const ANSWER_INVITE_OPTIONS = {
    sessionDescriptionHandlerOptions: {
        iceGatheringTimeout: 2000,
    },
};
export function callerLabelFromSession(session) {
    try {
        const ri = session.remoteIdentity;
        const name = ri.displayName?.trim();
        if (name)
            return name;
        const u = ri.uri.user;
        if (u)
            return String(u);
        return ri.uri.toString();
    }
    catch {
        return 'Unknown caller';
    }
}
const QUEUE_SIP_HEADER_NAMES = [
    'X-Queue-Name',
    'X-Queue',
    'Queue-Name',
    'Queue',
    'X-Asterisk-Queue',
    'X-QueueName',
];
const CALLED_SIP_HEADER_NAMES = [
    'X-Called-Number',
    'X-DID',
    'X-Original-DID',
    'Called-Number',
    'P-Called-Party-ID',
    'PCalledPartyID',
];
function readInvitationHeader(session, names) {
    if (!(session instanceof Invitation))
        return undefined;
    for (const name of names) {
        const raw = session.request.getHeader(name)?.trim();
        if (raw)
            return raw;
    }
    return undefined;
}
/** Pull a user part or digit string from a SIP name-addr or plain value. */
function digitsOrUriUser(value) {
    const angle = value.match(/<sip:([^@>;]+)/i);
    if (angle?.[1])
        return angle[1].trim();
    const bare = value.match(/sip:([^@>;]+)/i);
    if (bare?.[1])
        return bare[1].trim();
    const digits = value.replace(/\D/g, '');
    return digits || value.trim();
}
export function queueNameFromSession(session) {
    const raw = readInvitationHeader(session, QUEUE_SIP_HEADER_NAMES);
    if (!raw)
        return null;
    const cleaned = raw.replace(/^["']|["']$/g, '').trim();
    return cleaned || null;
}
export function calledNumberFromSession(session) {
    const raw = readInvitationHeader(session, CALLED_SIP_HEADER_NAMES);
    if (raw) {
        const parsed = digitsOrUriUser(raw);
        return parsed || null;
    }
    return null;
}
export function incomingRingFromSession(session) {
    return {
        callerLabel: callerLabelFromSession(session),
        queueName: queueNameFromSession(session),
        calledNumber: calledNumberFromSession(session),
    };
}
export function registerRejectMessage(response) {
    try {
        const { statusCode, reasonPhrase } = response.message ?? {};
        return `REGISTER rejected: ${statusCode ?? '?'} ${reasonPhrase ?? ''}`.trim();
    }
    catch {
        return 'REGISTER rejected by server.';
    }
}
export function sipResponseLine(prefix, response) {
    try {
        const { statusCode, reasonPhrase } = response.message ?? {};
        return `${prefix}: ${statusCode ?? '?'} ${reasonPhrase ?? ''}`.trim();
    }
    catch {
        return `${prefix} (no response details).`;
    }
}
export function looksLikeMissingDtlsFingerprintError(message) {
    const m = message.toLowerCase();
    return (m.includes('dtls fingerprint') ||
        (m.includes('sdp') && m.includes('setremotedescription')) ||
        (m.includes('invalidaccesserror') && m.includes('remotedescription')));
}
export function mapSipMediaException(e) {
    const msg = e instanceof Error ? e.message : String(e);
    const lower = msg.toLowerCase();
    if (lower.includes('insecure context') ||
        lower.includes('media devices not available')) {
        return ('Microphone/WebRTC requires a secure context (HTTPS or http://localhost). ' +
            'Open the app via https://… or http://127.0.0.1:3000 — not http://10.31.11.7 or another LAN IP over plain HTTP.');
    }
    return looksLikeMissingDtlsFingerprintError(msg) ? SDP_MISSING_DTLS_FINGERPRINT_HINT : msg;
}
/** Browsers block getUserMedia on http:// except localhost / 127.0.0.1. */
export function insecureContextSipHint() {
    if (typeof window === 'undefined' || window.isSecureContext) {
        return null;
    }
    const host = window.location.hostname;
    return (`WebRTC softphone requires HTTPS (or http://localhost). This page is ${window.location.protocol}//${host} — ` +
        'use TLS in production or http://127.0.0.1:3000 for local testing.');
}
